import React from "react";
import { cn } from "./cn";

/**
 * Estado vacío / error con jerarquía propia (icono → título → explicación →
 * acción), en lugar de un `<p class="text-gray-500">` suelto.
 */
export interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  tone?: "neutral" | "danger";
  className?: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  tone = "neutral",
  className,
}) => (
  <div
    className={cn(
      "flex flex-col items-center justify-center gap-3 rounded-card border border-dashed px-6 py-12 text-center",
      tone === "danger"
        ? "border-red-200 bg-red-100/40 dark:border-red-500/30 dark:bg-red-600/10"
        : "border-gray-200 bg-gray-50/60 dark:border-white/10 dark:bg-white/[0.02]",
      className
    )}
  >
    {icon ? (
      <div
        className={cn(
          "flex h-11 w-11 items-center justify-center rounded-full text-xl",
          tone === "danger"
            ? "bg-red-100 text-red-600 dark:bg-red-600/20 dark:text-red-300"
            : "bg-white text-gray-600 shadow-soft dark:bg-navy-800 dark:text-gray-300"
        )}
      >
        {icon}
      </div>
    ) : null}

    <div className="space-y-1">
      <p
        className={cn(
          "text-[15px] font-semibold",
          tone === "danger"
            ? "text-red-700 dark:text-red-300"
            : "text-gray-900 dark:text-white"
        )}
      >
        {title}
      </p>
      {description ? (
        <p className="mx-auto max-w-[42ch] text-sm leading-relaxed text-gray-700 dark:text-gray-300">
          {description}
        </p>
      ) : null}
    </div>

    {action ? <div className="mt-1">{action}</div> : null}
  </div>
);

export default EmptyState;
